import React, { useState, useRef } from "react";
import { Box, Typography, Button, TextField, IconButton, Paper, Slider } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import html2canvas from "html2canvas";

const initialData = [
  { label: "Secure Score for Defender", value: 30.51, color: "#5f2672" },
  { label: "Secure Score for Identity", value: 14.75, color: "#b30505" },
  { label: "Secure Score for Apps", value: 79.80, color: "#0d51d9" },
  { label: "Secure Score for Device", value: 47.98, color: "#ed790c" },
  { label: "Typical 101-1000 person company", value: 51.05, color: "#05b3a4" }
];

const BarChartBuilder = () => {
  const [data, setData] = useState(initialData);

  // Separate font states
  const [titleFontSize, setTitleFontSize] = useState(16);
  const [titleColor, setTitleColor] = useState("#1a1a1a");
  const [barFontSize, setBarFontSize] = useState(18);
  const [barPercentSize, setBarPercentSize] = useState(22);

  const [barFontColor, setBarFontColor] = useState("#1a1a1a");

  const chartRef = useRef();

  const handleChange = (index, key, value) => {
    const newData = [...data];
    newData[index][key] = value;
    setData(newData);
  };

  const handleAddRow = () => {
    setData([...data, { label: "", value: "", color: "#888888" }]);
  };

  const handleDeleteRow = (index) => {
    setData(data.filter((_, i) => i !== index));
  };

  const handleDownload = async () => {
    if (chartRef.current) {
      const canvas = await html2canvas(chartRef.current, { backgroundColor: "#fff" });
      const link = document.createElement("a");
      link.download = "bar-chart.jpg";
      link.href = canvas.toDataURL("image/jpeg", 1.0);
      link.click();
    }
  };

  const maxValue = 100;

  return (
    <Box
      sx={{
        height: "80vh",
        width: "93vw",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff"
      }}
    >
      {/* Toolbar */}
      <Box sx={{
        width: "100%",
        py: 2,
        px: { xs: 1, sm: 3 },
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid #ececec",
      }}>
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ color: titleColor, fontSize: 22 }}
        >
          Custom Bar Chart
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleDownload}
          startIcon={<DownloadIcon />}
          sx={{ borderRadius: 2, fontWeight: 500 }}
        >
          Download Chart
        </Button>
      </Box>

      {/* Font controls */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          alignItems: "center",
          px: { xs: 2, md: 4 },
          pt: 2,
          pb: 1,
          borderBottom: "1px solid #ececec",
        }}
      >
        {/* Title Controls */}
        <Box display="flex" alignItems="center" gap={1} mr={3}>
          <Typography variant="body2" sx={{ minWidth: 74 }}>Title Size:</Typography>
          <Slider
            min={16}
            max={36}
            step={1}
            value={titleFontSize}
            onChange={(_, val) => setTitleFontSize(val)}
            sx={{ width: 100 }}
          />
          <TextField
            size="small"
            type="number"
            value={titleFontSize}
            onChange={e => setTitleFontSize(Number(e.target.value))}
            inputProps={{ min: 10, max: 40, style: { width: 38, padding: 4, fontSize: 14 } }}
            sx={{ width: 46, mx: 1 }}
          />
          <Typography variant="body2">Title Color:</Typography>
          <input
            type="color"
            value={titleColor}
            onChange={e => setTitleColor(e.target.value)}
            style={{ width: 32, height: 32, border: "none", background: "none" }}
          />
        </Box>
        {/* Bar Label Controls */}
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" sx={{ minWidth: 84 }}>Bar Text Size:</Typography>
          <Slider
            min={12}
            max={28}
            step={1}
            value={barFontSize}
            onChange={(_, val) => setBarFontSize(val)}
            sx={{ width: 100 }}
          />
          <TextField
            size="small"
            type="number"
            value={barFontSize}
            onChange={e => setBarFontSize(Number(e.target.value))}
            inputProps={{ min: 10, max: 40, style: { width: 38, padding: 4, fontSize: 14 } }}
            sx={{ width: 46, mx: 1 }}
          />
          <Typography variant="body2">Bar Text Color:</Typography>
          <input
            type="color"
            value={barFontColor}
            onChange={e => setBarFontColor(e.target.value)}
            style={{ width: 32, height: 32, border: "none", background: "none" }}
          />
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          width: "100%",
        }}
      >
        {/* Chart Panel */}
        <Box
          ref={chartRef}
          sx={{
            flex: 2,
            minWidth: 320,
            p: { xs: 2, md: 4 },
            display: "flex",
            flexDirection: "column",
            gap: 4,
            alignItems: "flex-start",
            justifyContent: "center",
            borderRight: { md: "1px solid #ececec" },
            height: "100%",
            overflow: "hidden",
          }}
        >
          {data.map((row, idx) => (
            <Box key={idx} sx={{ width: "100%", mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ color: titleColor, fontSize: titleFontSize }}
                >
                  {row.label}
                </Typography>
                <Typography
                  fontWeight="bold"
                  sx={{ color: barFontColor, fontSize: barFontSize }}
                >
                  {row.value}%
                </Typography>
              </Box>
              <Box
                sx={{
                  width: "100%",
                  height: 18,
                  background: "#e0e0e0",
                  borderRadius: 0,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${Math.max(0, Math.min(row.value, maxValue))}%`,
                    height: "100%",
                    background: row.color,
                    borderRadius: 0,
                    transition: "width 0.3s",
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>

        {/* Table Panel */}
        <Box
          sx={{
            flex: 1.2,
            minWidth: 260,
            display: "flex",
            flexDirection: "column",
            p: { xs: 2, md: 3 },
            height: "100%",
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
            Data Points
          </Typography>
          <Paper
            elevation={0}
            sx={{
              bgcolor: "#fff",
              border: "1px solid #ececec",
              borderRadius: 2,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
            }}
          >
            {/* Table Header */}
            <Box display="flex" sx={{ borderBottom: "1px solid #ececec", bgcolor: "#fafbfc" }}>
              <Box sx={{ flex: 2, px: 2, py: 1, fontWeight: 600 }}>Label</Box>
              <Box sx={{ flex: 1, px: 2, py: 1, fontWeight: 600 }}>Value</Box>
              <Box sx={{ width: 46, px: 1, py: 1, fontWeight: 600 }}>Color</Box>
              <Box sx={{ width: 44 }} />
            </Box>
            {/* Table Body */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                px: 0.5,
                py: 1,
              }}
            >
              {data.map((row, idx) => (
                <Box key={idx} display="flex" alignItems="center" mb={0.5}>
                  <TextField
                    value={row.label}
                    size="small"
                    variant="outlined"
                    onChange={(e) => handleChange(idx, "label", e.target.value)}
                    sx={{ flex: 2, mx: 1, bgcolor: "#fafbfc" }}
                  />
                  <TextField
                    type="number"
                    value={row.value}
                    size="small"
                    variant="outlined"
                    onChange={(e) => handleChange(idx, "value", e.target.value)}
                    sx={{ flex: 1, mx: 1, bgcolor: "#fafbfc" }}
                  />
                  <input
                    type="color"
                    value={row.color}
                    onChange={(e) => handleChange(idx, "color", e.target.value)}
                    style={{ width: 36, height: 36, border: "none", background: "none" }}
                  />
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => handleDeleteRow(idx)}
                    disabled={data.length <= 1}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
            {/* Add Row Button */}
            <Box
              sx={{
                borderTop: "1px solid #ececec",
                px: 2,
                py: 1,
                textAlign: "center",
                bgcolor: "#fafbfc",
              }}
            >
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddRow}
                size="small"
                sx={{
                  borderRadius: 2,
                  fontWeight: 500,
                  px: 2
                }}
              >
                Add Row
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default BarChartBuilder;
